import { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { Divider } from 'react-native-elements';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { Button } from '@/components/ui/therapist/Button';
import { Card } from '@/components/ui/therapist/Card';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/ui/therapist/Input';
import { PatientSelect } from '@/components/ui/therapist/PatientSelect';
import { Text } from '@/components/ui/therapist/Text';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useStoreState } from '@/stores/store';
import { useSubmitState } from '@/hooks/useSubmitState';
import { assignTemplate } from '@/api/services/templates';
import { IS_ANDROID } from '@/utils/platform';

import styles from './styles';
import colors from '@/config/colors';

export const TemplateAssign = ({
  route,
  navigation,
}: StackScreenProps<TherapistStackParamList, 'TemplateAssign'>) => {
  const { template } = route.params;
  const [appDisplayName, setAppDisplayName] = useState<string>(
    template.setType === 'Word' ? 'Wörterset' : 'Sätzeset',
  );
  const [name, setName] = useState<string>(template.name);
  const [patientIds, setPatientIds] = useState<string[]>([]);
  const { isTherapistDemo } = useStoreState((store) => store);
  const { showNotification } = useNotification();
  const insets = useSafeAreaInsets();
  const bottomInset = IS_ANDROID ? insets.bottom : 0;

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <AppHeaderAction
          primary
          onPress={navigation.goBack}
          icon={<Icon name="close" scale={1.2} />}
          text="Abbrechen"
          disabled={isSubmitting}
        />
      ),
    });
  }, []);

  const assignToPatients = async () => {
    if (!isFormValid()) {
      showNotification({
        type: 'error',
        message:
          'Bitte füllen Sie alle Pflichtfelder aus und wählen Sie mindestens eine Patient:in aus.',
      });
      return;
    }

    if (isTherapistDemo) {
      showNotification({
        type: 'error',
        message: 'Diese Aktion ist während einer Demo nicht möglich.',
      });
      return;
    }

    try {
      const payload = {
        appDisplayName,
        name,
        patientIds,
      };
      await assignTemplate({ payload, templateId: template.id });
      showNotification({
        type: 'success',
        message: `Die Vorlage ${name} wurde ${payload.patientIds.length > 0 ? `${payload.patientIds.length} Patient:innen` : `${payload.patientIds.length} Patient:in`}  zugewiesen.`,
      });
      navigation.goBack();
    } catch {
      showNotification({
        type: 'error',
        message:
          'Fehler beim Zuweisen der Vorlage. Bitte versuchen Sie es erneut.',
      });
    }
  };

  const isFormValid = (): boolean => {
    return (
      name.trim().length > 0 &&
      appDisplayName.trim().length > 0 &&
      patientIds.length > 0
    );
  };

  const { isSubmitting, submit } = useSubmitState({
    onSubmit: assignToPatients,
  });

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: bottomInset }}>
      <View style={styles.templateAssignWrapper}>
        <Card bottomDent styleBody={{ padding: 0 }}>
          <View style={{ padding: 20 }}>
            <Text style={styles.sectionHeader}>Vorlage zuweisen</Text>
            <Text>
              Bitte wählen Sie die Patient:innen aus, denen Sie die Vorlage
              zuweisen möchten.
            </Text>
          </View>
          <Divider color={colors.lightGrey} width={2} />
          <View style={{ paddingHorizontal: 20, paddingTop: 10 }}>
            <Input
              value={name}
              onChange={(text) => setName(text)}
              label="Set Name"
              required
              additionalInfo="Ein eindeutiger Name, der Ihnen hilft, das Set schnell und
              problemlos wiederzuerkennen."
              style={{ paddingTop: 10 }}
            />
          </View>
          <View style={{ paddingHorizontal: 20 }}>
            <Input
              value={appDisplayName}
              onChange={(text) => setAppDisplayName(text)}
              label="Set Name für Eigentraining"
              additionalInfo="Ein verständlicher Name, der in der Patienten-App angezeigt
              wird (maximal 16 Zeichen)."
              maxLength={16}
              required
              style={{ paddingTop: 10 }}
            />
          </View>
        </Card>
        <PatientSelect patientIds={patientIds} setPatientIds={setPatientIds} />
        <View style={styles.footerWrapper}>
          <Button
            disabled={isSubmitting}
            label="Abbrechen"
            onPress={() => navigation.goBack()}
            look="ghost"
          />
          <Button disabled={isSubmitting} label="Zuweisen" onPress={submit} />
        </View>
      </View>
    </ScrollView>
  );
};
